import { useMemo, useCallback } from 'react'

import { unique } from 'utils/list'
import { generateSearchNeedle } from 'utils/search'

export function buildSearchStringMap<T>(
  data: T[],
  searchMapFn: (o) => string[]
) {
  return data.reduce((searchStrings: Record<string, T[]>, item) => {
    const terms = searchMapFn(item)
    terms.forEach((term) => {
      if (searchStrings[term]) {
        if (!searchStrings[term].includes(item)) {
          searchStrings[term].push(item)
        }
      } else {
        searchStrings[term] = [item]
      }
    })

    return searchStrings
  }, {})
}

export function search<T>(
  term: string,
  objects: T[],
  searchStringMap: Record<string, T[]>
) {
  if (term.length) {
    const needle = term.toLowerCase()
    const haystack = searchStringMap

    const found = Object.keys(haystack).reduce((accum: T[], key) => {
      if (key.toLowerCase().includes(needle)) {
        Array.prototype.push.apply(accum, haystack[key])
      }
      return accum
    }, [])

    return unique(found)
  }

  return objects
}

export default function useSearch<T>(
  data: T[] = [],
  searchMapFn = generateSearchNeedle
) {
  const searchStringMap = useMemo(
    () => buildSearchStringMap(data, searchMapFn),
    [data, searchMapFn]
  )

  const searchFn = useCallback(
    (term: string) => search(term.trim().toLowerCase(), data, searchStringMap),
    [data, searchStringMap]
  )

  return searchFn
}
